import { useState, useMemo } from 'react';
import {
  makeStyles, tokens, Title2, Body1, Spinner, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { AreaChart, Area, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useCfd } from '../../../api/hooks';
import type { CfdDayRow } from '../../../api/types';

// FEAT-019 Cumulative Flow Diagram. Stacked area chart showing how many WIs sat in each
// state per day over the last 90 days. Widening bands flag WIP buildup in a state.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  chartCard: {
    height: '26rem',
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

// State → series colour. Token-only — any state not in the lookup falls back to a
// brand-neutral default so unexpected ADO states still render.
const STATE_COLOURS: Record<string, string> = {
  'New': tokens.colorNeutralBackground5,
  'Active': tokens.colorBrandBackground,
  'Resolved': tokens.colorPaletteYellowBackground3,
  'Closed': tokens.colorPaletteGreenBackground3,
  'Done': tokens.colorPaletteGreenBackground3,
};

export function CfdPage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useCfd(projectId, 90);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.days_.map((row: CfdDayRow) => ({ day: row.day, ...row.countsByState }));
  }, [data]);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Cumulative Flow</Title2>
        <Body1 className={styles.hint}>
          Stacked area chart by state, last 90 days. Bands that widen day-over-day point at WIP buildup in that state — usually Code Review or Awaiting UAT.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project.</Body1>
      ) : isLoading ? (
        <Spinner label="Loading flow…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.days_.length === 0 ? (
        <Body1>Analytics has not produced snapshots for this project yet — try again in 24 h.</Body1>
      ) : (
        <div className={styles.chartCard}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              {data.states.map((state: string) => (
                <Area
                  key={state}
                  type="monotone"
                  dataKey={state}
                  stackId="1"
                  stroke={STATE_COLOURS[state] ?? tokens.colorNeutralStroke1}
                  fill={STATE_COLOURS[state] ?? tokens.colorBrandBackground2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
